import { Beneficiary } from "../../../../types/beneficiaries";
import { nameOnlySchema } from "../../../../utils/validateNameSchema";

/**
 * Validates if a beneficiary has all required fields filled
 */
export const validateBeneficiary = (beneficiary: Beneficiary): boolean => {
  return !!(
    beneficiary.firstName &&
    beneficiary.lastName &&
    beneficiary.relationship &&
    beneficiary.dateOfBirth
  );
};

/**
 * Check if all beneficiary names are valid using the Zod schema
 */
export const hasValidNames = (beneficiaries: Beneficiary[]): boolean => {
  return beneficiaries.every(beneficiary => {
    if (!beneficiary.firstName || !beneficiary.lastName) return false;
    
    try {
      nameOnlySchema.parse({
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
      });
      return true;
    } catch {
      return false;
    }
  });
};

/**
 * Validates if all beneficiaries in an array are valid
 */
export const validateAllBeneficiaries = (
  beneficiaries: Beneficiary[]
): boolean => {
  return beneficiaries.every(validateBeneficiary);
};

/**
 * Checks if a beneficiary is new (not from API)
 */
export const isNewBeneficiary = (
  beneficiary: Beneficiary,
  initialBeneficiaries: Beneficiary[]
): boolean => {
  return !initialBeneficiaries.some((b) => b.id === beneficiary.id);
};

/**
 * Creates an empty beneficiary object
 */
export const createEmptyBeneficiary = (): Beneficiary => ({
  id: null,
  tempId: `temp-${crypto.randomUUID()}`,
  firstName: "",
  lastName: "",
  relationship: "" as const,
  dateOfBirth: "",
});

/**
 * Filters beneficiaries that have changed compared to initial state
 */
export const detectBeneficiaryChanges = (
  current: Beneficiary[],
  initial: Beneficiary[]
): Beneficiary[] => {
  const existingBeneficiaries = current.filter(
    (b) => !isNewBeneficiary(b, initial)
  );

  return existingBeneficiaries.filter((b) => {
    const original = initial.find((orig) => orig.id === b.id);
    return (
      !original ||
      b.firstName !== original.firstName ||
      b.lastName !== original.lastName ||
      b.relationship !== original.relationship ||
      b.dateOfBirth !== original.dateOfBirth
    );
  });
};

/**
 * Formats beneficiaries for API consumption
 */
export const formatBeneficiariesForAPI = (
  beneficiaries: Beneficiary[],
  userAccountID: string,
  isUpdate: boolean = false
) => {
  return beneficiaries.map((beneficiary) => ({
    ...(isUpdate && { minorAccountID: beneficiary.id }),
    userAccountID,
    parentUserAccountID: userAccountID,
    firstName: beneficiary.firstName ?? "",
    middleName: beneficiary.middleName ?? "",
    lastName: beneficiary.lastName ?? "",
    dateOfBirth: beneficiary.dateOfBirth ?? "",
    relationship: beneficiary.relationship ?? "",
    changeReason: isUpdate ? "Updating child" : "Adding child",
  }));
};

/**
 * Checks if any beneficiaries have valid data (for progress reset logic)
 */
export const hasValidBeneficiaries = (
  beneficiaries: Beneficiary[]
): boolean => {
  return beneficiaries.some(validateBeneficiary);
};

/**
 * Finds a beneficiary to delete by comparing all fields (for unsaved beneficiaries)
 */
export const findBeneficiaryIndex = (
  beneficiaries: Beneficiary[],
  targetBeneficiary: Beneficiary
): number => {
  return beneficiaries.findIndex(
    (b) =>
      b.firstName === targetBeneficiary.firstName &&
      b.lastName === targetBeneficiary.lastName &&
      b.relationship === targetBeneficiary.relationship &&
      b.dateOfBirth === targetBeneficiary.dateOfBirth
  );
};
